import React, { ChangeEvent, useState } from 'react';
import {
  machineListsData,
  modifyMachineData,
  modifyMachineSortOrderData,
} from 'types/perMachineTypes';
import { Button, Modal } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modifyMachine, modifyMachineSortOrder } from '@apis/perMachine';
import { PERMACHINE } from './queryKey/queryKey';
import { AxiosHeaders } from 'axios';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { imgTypes } from '@dataTypes/imgTypes';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';
import { RiArrowUpDownFill } from 'react-icons/ri';
import storeInfo from '../../recoil/storeInfo';

interface MachineListItemProps {
  item: machineListsData;
  tempList: machineListsData[];
  setTempList: React.Dispatch<React.SetStateAction<machineListsData[]>>;
  originalList: machineListsData[];
}

const MachineListItem = ({ item, tempList, setTempList, originalList }: MachineListItemProps) => {
  const currentStoreInfo = useRecoilValue(storeInfo);
  const storeId = currentStoreInfo?.currentStoreId;
  const queryClient = useQueryClient();
  const setErrorState = useSetRecoilState(errorOrYesModalState);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const { mutateAsync: modifyMachineAsync } = useMutation(
    (data: modifyMachineData) => modifyMachine(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PERMACHINE.MACHINELISTS]);
      },
      onError: (error: AxiosHeaders) => {
        if (error.response.status === 400) {
          setErrorState({
            status: true,
            imgType: imgTypes.ERROR,
            title: error.response.data.message,
            modalBG: true,
          });
        }
      },
    },
  );

  const { mutateAsync: modifyMachineSortOrderAsync } = useMutation(
    (data: { storeId: number; data: modifyMachineSortOrderData }) =>
      modifyMachineSortOrder(data.storeId, data.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PERMACHINE.MACHINELISTS]);
      },
    },
  );

  const [showModify, setShowModify] = useState(false);
  const [inputVal, setInputVal] = useState<string>(item.deviceAlias);
  const [inputSortOrder, setInputSortOrder] = useState<number>(item.sortOrder);

  const inputSortOrderChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const newOrder = Number(value || 0);

    setInputSortOrder(newOrder);

    setTempList((prev) =>
      prev.map((machine) =>
        machine.deviceId === item.deviceId ? { ...machine, sortOrder: newOrder } : machine,
      ),
    );
  };

  const inputChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setInputVal(value);

    event.target.style.height = 'auto';
    event.target.style.height = `${event.target.scrollHeight}px`;

    setTempList((prev) =>
      prev.map((machine) =>
        machine.deviceId === item.deviceId ? { ...machine, deviceAlias: value } : machine,
      ),
    );
  };

  const handleModifyClick = () => {
    setShowModify(true);
  };

  const cancelHandler = () => {
    setShowModify(false);
    setInputVal(item.deviceAlias);
    setInputSortOrder(item.sortOrder);
  };

  const confirmHandler = () => {
    const tempItem = tempList.find((v) => v.deviceId === item.deviceId);
    const originalItem = originalList.find((v) => v.deviceId === item.deviceId);
    if (!tempItem || !originalItem) return;

    const maxOrder = tempList.length;

    if (tempItem.sortOrder < 1 || tempItem.sortOrder > maxOrder) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '존재하지 않는 순번입니다.',
        text: `1부터 최대 순번인 ${maxOrder}까지 입력 가능합니다.`,
        modalBG: true,
      });
      return;
    }

    const isAliasChanged = tempItem.deviceAlias !== originalItem.deviceAlias;

    const isSortOrderChanged = tempItem.sortOrder !== originalItem.sortOrder;

    if (!isAliasChanged && !isSortOrderChanged) {
      setShowModify(false);
      return;
    }

    setShowDuplicateModal(true);
  };

  const confirmDuplicateModalHandler = async () => {
    const tempItem = tempList.find((v) => v.deviceId === item.deviceId);
    const originalItem = originalList.find((v) => v.deviceId === item.deviceId);
    if (!tempItem || !originalItem) return;

    const isAliasChanged = inputVal !== originalItem.deviceAlias;
    const isSortOrderChanged = tempItem.sortOrder !== originalItem.sortOrder;

    try {
      const target = tempList.find((list) => list.deviceId === item.deviceId);
      const duplicated = tempList.find(
        (list) => list.deviceId !== item.deviceId && list.sortOrder === inputSortOrder,
      );

      const originalTarget = originalList.find((list) => list.deviceId === item.deviceId);

      if (!target || !originalTarget) return;

      if (!storeId) {
        setErrorState({
          status: true,
          imgType: imgTypes.ERROR,
          title: '지점 정보를 찾을 수 없습니다.',
          text: '지점 정보를 찾을 수 없습니다. 관리자에게 문의해주세요.',
          modalBG: true,
        });
        return;
      }

      if (isAliasChanged) {
        await modifyMachineAsync({
          alias: inputVal.toString(),
          deviceId: item.deviceId,
        });
      }

      if (isSortOrderChanged) {
        const originalOrder = originalTarget.sortOrder;

        const updatedList = tempList.map((list) => {
          // 현재 수정 중인 아이템 → 변경 순번
          if (list.deviceId === target.deviceId) {
            return { ...list, sortOrder: inputSortOrder };
          }

          // 중복된 아이템 → 기존 순번(1)
          if (list.deviceId === duplicated?.deviceId) {
            return { ...list, sortOrder: originalOrder };
          }

          return list;
        });

        const filteredList: modifyMachineSortOrderData = {
          orders: updatedList.map((list) => ({
            catId: Number(list.catId),
            sortOrder: list.sortOrder,
          })),
        };
        await modifyMachineSortOrderAsync({ storeId: storeId ?? 0, data: filteredList });
        setTempList(updatedList);
      }

      setShowDuplicateModal(false);
      setShowModify(false);
    } catch (error) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '오류가 발생했습니다.',
        text: '오류가 발생했습니다. 관리자에게 문의해주세요.',
        modalBG: true,
      });
    }
  };

  const isDuplicatedOrder = tempList.some(
    (v) => v.deviceId !== item.deviceId && v.sortOrder === inputSortOrder,
  );

  const duplicatedMachine = tempList.find(
    (v) => v.deviceId !== item.deviceId && v.sortOrder === inputSortOrder,
  );

  return (
    <>
      <div className="sales_item_wrapper">
        <div className="sales_date">
          {!showModify ? (
            <span className="order">{item.sortOrder}</span>
          ) : (
            <input
              type="text"
              className={`input_text sort_order ${isDuplicatedOrder ? 'error' : ''}`}
              value={inputSortOrder}
              onChange={inputSortOrderChangeHandler}
              autoFocus={true}
            />
          )}
          <span className="title">{item.catId}</span>
          {!showModify ? (
            <span className="text">{item.deviceAlias}</span>
          ) : (
            <textarea
              className="input_text"
              value={inputVal}
              rows={1}
              onChange={inputChangeHandler}
            />
          )}
          {!showModify && (
            <Button className="tooltip_icon" onClick={handleModifyClick}>
              수정
            </Button>
          )}
        </div>
        {showModify && (
          <div className="btn_wrapper">
            <Button className="cancel" onClick={cancelHandler}>
              취소
            </Button>
            <Button className="confirm" onClick={confirmHandler}>
              완료
            </Button>
          </div>
        )}
      </div>

      <Modal
        id={'duplicate_modal'}
        onHide={() => setShowDuplicateModal(false)}
        show={showDuplicateModal}
        size="lg"
        centered
      >
        <Modal.Header>
          <Modal.Title>단말기 정보 변경</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="duplicate_modal_text">하단 내용으로 변경됩니다.</div>

          {inputVal !== item.deviceAlias && (
            <div className="duplicate_modal_text_wrapper">
              <div className="text">
                <span className="order">기존 별칭</span>
                <span className="machine_name">{item.deviceAlias}</span>
              </div>

              <span className="arrow_icon">
                <RiArrowUpDownFill />
              </span>

              <div className="text change_order">
                <span className="order">변경 별칭</span>
                <span className="machine_name">{inputVal}</span>
              </div>
            </div>
          )}

          {inputSortOrder !== item.sortOrder && (
            <div className="duplicate_modal_text_wrapper">
              <div className="text">
                <span className="order">기존 순번</span>
                <span className="machine_name">
                  {inputVal !== item.deviceAlias
                    ? item.sortOrder
                    : `${item.sortOrder}. ${item.deviceAlias}`}
                </span>
              </div>

              <span className="arrow_icon">
                <RiArrowUpDownFill />
              </span>

              <div className="text change_order">
                <span className="order">변경 순번</span>
                <span className="machine_name">
                  {inputVal !== item.deviceAlias
                    ? inputSortOrder
                    : `${inputSortOrder}. ${duplicatedMachine?.deviceAlias}`}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="duplicate_modal_footer">
          <Button className="ok_btn" onClick={confirmDuplicateModalHandler}>
            확인
          </Button>
          <Button className="cancel_btn" onClick={() => setShowDuplicateModal(false)}>
            취소
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MachineListItem;
